import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { palette } from 'styled-tools';

import { fontSize, FontSizes, fontWeight, FontWeights, minWidth } from '@calm-web/design-system';

import { useFormattedCurrency } from '@/hooks/purchase';
import { useFormattedPriceWithTax } from '@/hooks/purchase/useFormattedPriceWithTax';
import { Cents } from '@/utils/prices/types';

import messages from './messages';

const QuantityWrap = styled.div`
	width: 100%;
	${fontSize(FontSizes.base)};
	display: flex;
	align-items: center;
	margin-top: 16px;
	color: ${palette('gray7')};
`;

const Quantity = styled.span`
	${fontWeight(FontWeights.Medium)};
	margin-right: 0.5rem;
	${minWidth('tablet')} {
		margin-right: 1rem;
	}
`;

const TotalWrap = styled.div`
	width: 100%;
	flex-direction: column;
	display: flex;
	align-items: flex-end;
	text-align: right;

	${minWidth('tablet')} {
		flex-direction: row;
		align-items: flex-end;
		justify-content: flex-end;
		text-align: left;
	}
`;

const OriginalTotal = styled.span`
	font-size: 14px;
	${fontWeight(FontWeights.Regular)};
	text-decoration: line-through;

	${minWidth('tablet')} {
		margin: 0 12px;
	}
`;

const Total = styled.span`
	${fontWeight(FontWeights.Medium)};
	margin-left: auto;

	${minWidth('tablet')} {
		margin-left: 0;
	}
`;

const Select = styled.select`
	appearance: none;
	height: 3rem;
	width: auto;
	border: 1px solid ${palette('gray2')};
	border-radius: 0.5rem;
	background-color: ${palette('white')};
	text-align: center;
	${fontSize(FontSizes.base)};
	padding: 0.25rem;
	padding-top: 0.2rem;
	min-width: 2.5rem;
`;

interface QuantitySelectorProps {
	quantity: number;
	originalPrice?: Cents | null;
	price: Cents;
	offerFreeCount: number;
	onChangeQuantity: (quantity: number) => void;
}

const QuantitySelector = ({
	quantity,
	originalPrice,
	price,
	offerFreeCount,
	onChangeQuantity,
}: QuantitySelectorProps) => {
	const { formatMessage } = useIntl();
	const formatCurrency = useFormattedCurrency();

	const selectOptions = useMemo(() => Array.from(Array(25).keys()).map(k => `${k + 1}`), []);
	const onChangeQuantityWrapped = (e: React.FormEvent<HTMLSelectElement>) => {
		const valueStr = e.currentTarget.value && e.currentTarget.value.length > 0 ? e.currentTarget.value : '1';
		onChangeQuantity(parseInt(valueStr, 10));
	};

	const numFree = offerFreeCount === 0 ? 0 : Math.floor(quantity / offerFreeCount);
	const totalPrice = quantity * price; // in cents USD
	const value = `${quantity || '1'}`;
	const offerReminder =
		numFree === 0 ? null : (
			<div className="offer-reminder">{formatMessage(messages.quantitySelectorNumFree, { numFree })}</div>
		);

	const priceWithTax = useFormattedPriceWithTax(totalPrice, 'USD');

	return (
		<QuantityWrap>
			<Quantity className="quantity">{formatMessage(messages.quantitySelectorTitle)}</Quantity>
			<Select value={value} onChange={onChangeQuantityWrapped}>
				{selectOptions.map(k => (
					<option key={k} value={k}>
						{k}
					</option>
				))}
			</Select>
			{offerReminder}
			<TotalWrap>
				{originalPrice && (
					<OriginalTotal>
						{formatMessage(messages.quantitySelectorPrice, {
							price: `${formatCurrency(originalPrice, 'USD')} USD`,
						})}
					</OriginalTotal>
				)}
				<Total>
					{formatMessage(messages.quantitySelectorPrice, {
						price: priceWithTax,
					})}
				</Total>
			</TotalWrap>
		</QuantityWrap>
	);
};

export default QuantitySelector;
